import {logLoginEvent} from '@/common/analytics-events';
import {ArrowButton} from '@/components/Buttons/ArrowButton';
import {setAccessToken, setCurrentAccountId} from '@/user/user-utils';
import {yupResolver} from '@hookform/resolvers/yup';
import {signInWithEmailAndPassword} from 'firebase/auth';
import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {Link, useNavigate} from 'react-router-dom';
import * as yup from 'yup';
import {auth as firebaseAuth} from '../../firebase/config';

export const MLSAuthEmailSignIn = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const validationSchema = yup.object().shape({
    email: yup.string().required('Email is required').email('Email is invalid'),
    password: yup
      .string()
      .required('Password is required')
      .min(6, 'Password must be at least 6 characters')
      .max(40, 'Password must not exceed 40 characters'),
  });

  const {
    register,
    handleSubmit,
    formState: {errors},
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const resetError = () => setError('');

  const handleEmailSignIn = async (data: any) => {
    setIsLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(firebaseAuth, data.email, data.password);
      const authUser = userCredential.user;

      setAccessToken((await authUser.getIdTokenResult()).token);
      setCurrentAccountId(authUser.uid);

      logLoginEvent('emailAndPassword', {
        userUid: userCredential?.user?.uid,
        operationType: userCredential?.operationType,
      });

      navigate('/profiles');
    } catch (err: any) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-black">
      <div className="flex relative flex-col min-h-screen max-w-lg mx-auto flex-1 text-white items-center justify-center px-4">
        <ArrowButton
          className="absolute top-4 left-2"
          onClick={() => navigate('/auth/signin')}
          variant="secondary"
          arrowDirection="left"
        />

        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-250px)] text-center">
          <h1 className="text-4xl md:text-5xl font-teko font-bold">SIGN IN WITH EMAIL</h1>
          <p className="text-base md:text-lg font-helvetica font-thin mt-1 mb-10">
            Enter your email and password to sign in
          </p>

          <form onSubmit={handleSubmit(handleEmailSignIn)} className="w-full max-w-md">
            <div className="mb-4">
              <label className="block text-sm opacity-30 text-left mb-1">Email</label>
              <input
                type="email"
                {...register('email', {
                  onChange: resetError,
                })}
                className="w-full p-3 text-base rounded bg-white text-black"
                placeholder="Enter your email"
                data-test-id="signin-with-email-email-input"
              />
              {errors.email && (
                <p data-test-id="email-error-message" className="text-mls-error text-sm text-left mt-1">
                  {errors.email.message as string}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm opacity-30 text-left mb-1">Password</label>
              <input
                type="password"
                {...register('password', {
                  onChange: resetError,
                })}
                className="w-full p-3 text-base rounded bg-white text-black"
                placeholder="Enter your password"
                data-test-id="signin-with-email-password-input"
              />
              {errors.password && (
                <p data-test-id="password-error-message" className="text-mls-error text-sm text-left mt-1">
                  {errors.password.message as string}
                </p>
              )}
            </div>

            {error && (
              <p data-test-id="email-login-error-message" className="text-mls-error text-sm text-left mt-1 mb-4">
                {error}
              </p>
            )}

            <ArrowButton
              data-test-id="signin-with-email-submit-button"
              className="mt-8 self-end float-right"
              type="submit"
              loading={isLoading}
              hasError={!!error}
            />
            <div className="absolute bottom-16 left-0 right-0 flex justify-center">
              <Link className="text-sm text-white underline" to="/auth/forget-password">
                Forgot your password?
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
